import { useAuthInfo } from "@propelauth/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Save, Send, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import MarkdownRenderer from "../../../../../components/agno/MarkdownRenderer";
import ModelSelector from "../../../../../components/agno/ModelSelector";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../../components/ui/alert-dialog";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../components/ui/card";
import { Input } from "../../../../../components/ui/input";
import { Textarea } from "../../../../../components/ui/textarea";
import { useBusinessContext } from "../../../../../hooks/useBusinessContext";
import { cn } from "../../../../../lib/utils";
import { listComputedLabels } from "../../../../../services/computedTraits/computedTraitsEndpoints";
import type { ComputedLabel } from "../../../../../services/computedTraits/computedTraitsEndpoints.interface";
import {
  readHealthScoreSettings,
  updateHealthScoreSettings,
} from "../../../../../services/healthScoreSettings/healthScoreSettingsEndpoints";
import type {
  HealthScoreModel,
  HealthScoreSettings,
} from "../../../../../services/healthScoreSettings/healthScoreSettingsEndpoints.interface";
import {
  ChatMessageAvatar,
  ChatSenderLabel,
  getChatUserInitials,
} from "../../../../../shared/chat/ChatMessageIdentity";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import notify from "../../../../../utils/notify";
import { getHealthModelDefaultTitle } from "../healthScoreHelpers";
import {
  buildHealthModelBuilderContext,
  buildHealthModelBuilderKickoffPrompt,
} from "../healthScoreBuilderHelpers";
import {
  buildAvailableHealthScopes,
  buildDefaultHealthScoreSettings,
  createHealthModelFromScope,
  decodeHealthScopeKeyFromRoute,
  formatHealthTimestamp,
  hydrateHealthScoreSettings,
  normalizeHealthScoreSettings,
} from "../healthScoreSettingsState";
import useHealthModelBuilderStream from "../hooks/useHealthModelBuilderStream";

type HealthScoreChatBuilderProps = {
  onBack?: () => void;
};

const HealthScoreChatBuilder = ({ onBack }: HealthScoreChatBuilderProps) => {
  const { scopeKey: encodedScopeKey } = useParams();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const authInfo: any = useAuthInfo();
  const userInitials = getChatUserInitials(authInfo?.user);
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const kickoffRef = useRef<string | null>(null);
  // Snapshot of the document before a generation run, so a failed run can be reverted
  const preRunSnapshotRef = useRef<{ content: string; lastGeneratedAt: string | null } | null>(null);
  const wasStreamingRef = useRef(false);

  const [input, setInput] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [documentTitle, setDocumentTitle] = useState("");
  const [documentContent, setDocumentContent] = useState("");
  const [modelKey, setModelKey] = useState<string | null>(null);
  const [lastGeneratedAt, setLastGeneratedAt] = useState<string | null>(null);

  const workspaceId = workspace?.id;
  const scopeKey = decodeHealthScopeKeyFromRoute(encodedScopeKey);

  const businessContextQuery = useBusinessContext(workspaceId || "");

  const settingsQuery = useQuery({
    queryKey: ["health-score-settings", workspaceId],
    queryFn: async () => {
      const response = await readHealthScoreSettings({ workspace_id: workspaceId! });
      return response.data;
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });

  const computedLabelsQuery = useQuery({
    queryKey: ["computed-labels", workspaceId],
    queryFn: async () => {
      const response = await listComputedLabels({ workspace_id: workspaceId! });
      return response.data as ComputedLabel[];
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 60,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: HealthScoreSettings) =>
      updateHealthScoreSettings({
        workspace_id: workspaceId!,
        payload,
      }),
  });

  const savedSettings = useMemo(
    () => hydrateHealthScoreSettings(settingsQuery.data || buildDefaultHealthScoreSettings()),
    [settingsQuery.data],
  );

  const availableScopes = useMemo(
    () =>
      buildAvailableHealthScopes({
        businessContext: businessContextQuery.data,
        labels: computedLabelsQuery.data || [],
        savedModels: savedSettings.models,
      }),
    [businessContextQuery.data, computedLabelsQuery.data, savedSettings.models],
  );

  const selectedScope = useMemo(
    () => availableScopes.find((scope) => scope.scope_key === scopeKey),
    [availableScopes, scopeKey],
  );

  const savedModel = useMemo(
    () => savedSettings.models.find((model) => model.scope_key === scopeKey),
    [savedSettings.models, scopeKey],
  );

  const builderContext = useMemo(
    () => (
      selectedScope
        ? buildHealthModelBuilderContext({
            scope: selectedScope,
            businessContext: businessContextQuery.data,
            documentTitle,
            documentContent,
          })
        : null
    ),
    [businessContextQuery.data, documentContent, documentTitle, selectedScope],
  );

  const {
    messages: streamedMessages,
    isAssistantTyping,
    isStreaming,
    streamError,
    latestDraft,
    sendMessage,
  } = useHealthModelBuilderStream({
    scopeKey: selectedScope?.scope_key || scopeKey || null,
    builderContext,
  });

  const savedTitle = savedModel?.document_title || (selectedScope ? getHealthModelDefaultTitle(selectedScope) : "");
  const savedContent = savedModel?.document_content || "";
  const hasDraftChanges = documentTitle.trim() !== savedTitle.trim()
    || documentContent.trim() !== savedContent.trim()
    || (modelKey || "") !== (savedModel?.model_key || "")
    || (lastGeneratedAt || "") !== (savedModel?.last_generated_at || "");
  const isBusy = isStreaming || saveMutation.isLoading;

  // Warn on browser/tab close with unsaved draft changes
  useEffect(() => {
    if (!hasDraftChanges) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasDraftChanges]);

  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  const handleBack = useCallback(() => {
    if (hasDraftChanges) {
      setShowUnsavedWarning(true);
    } else {
      onBack?.();
    }
  }, [hasDraftChanges, onBack]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [documentContent, streamedMessages]);

  useEffect(() => {
    if (!selectedScope || settingsQuery.isLoading) return;

    setDocumentTitle(savedModel?.document_title || getHealthModelDefaultTitle(selectedScope));
    setDocumentContent(savedModel?.document_content || "");
    setModelKey(savedModel?.model_key || null);
    setLastGeneratedAt(savedModel?.last_generated_at || null);
    kickoffRef.current = null;
  }, [
    savedModel?.document_content,
    savedModel?.document_title,
    savedModel?.model_key,
    savedModel?.last_generated_at,
    selectedScope,
    settingsQuery.isLoading,
  ]);

  useEffect(() => {
    if (!selectedScope) return;
    if (settingsQuery.isLoading || businessContextQuery.isLoading || computedLabelsQuery.isLoading) return;
    if (isStreaming) return;
    if (streamedMessages.length > 0) return;
    if (documentTitle !== savedTitle || documentContent !== savedContent) return;

    const kickoffKey = `${workspaceId || "workspace"}:${selectedScope.scope_key}`;
    if (kickoffRef.current === kickoffKey) return;

    kickoffRef.current = kickoffKey;
    void sendMessage(
      buildHealthModelBuilderKickoffPrompt({
        scope: selectedScope,
        hasExistingDraft: Boolean(savedModel?.document_content?.trim()),
      }),
      { omitUserMessage: true },
    );
  }, [
    businessContextQuery.isLoading,
    computedLabelsQuery.isLoading,
    isStreaming,
    documentContent,
    documentTitle,
    savedContent,
    savedModel?.document_content,
    savedTitle,
    selectedScope,
    sendMessage,
    settingsQuery.isLoading,
    streamedMessages.length,
    workspaceId,
  ]);

  // Snapshot the document at the moment a generation run begins, so we can
  // restore it if the run fails partway through (no half-applied draft).
  useEffect(() => {
    if (isStreaming && !wasStreamingRef.current) {
      preRunSnapshotRef.current = { content: documentContent, lastGeneratedAt };
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, documentContent, lastGeneratedAt]);

  // Sync latestDraft from the agent's inline code fence → document panel.
  // Skip while a run is in an error state — a failed scoring must not be
  // committed to the document or stamped as a successful generation.
  useEffect(() => {
    if (streamError) return;
    if (!latestDraft?.content) return;
    setDocumentContent(latestDraft.content);
    if (!latestDraft.isStreaming) {
      setLastGeneratedAt(new Date().toISOString());
    }
  }, [latestDraft?.content, latestDraft?.isStreaming, streamError]);

  // On a failed run, revert any partial draft back to the pre-run document and
  // surface the failure. Nothing about the failed run is left saveable.
  useEffect(() => {
    if (!streamError) return;
    const snapshot = preRunSnapshotRef.current;
    if (snapshot) {
      setDocumentContent(snapshot.content);
      setLastGeneratedAt(snapshot.lastGeneratedAt);
    }
    notify({
      type: "failure",
      heading: "Generation failed",
      messageList: [streamError],
    });
  }, [streamError]);

  const handleSaveDraft = useCallback(async () => {
    if (!workspaceId || !selectedScope || saveMutation.isLoading) return;

    const content = documentContent.trim();
    if (!content) {
      notify({
        type: "failure",
        heading: "Draft is empty",
        messageList: ["Generate or write a health document before saving the draft."],
      });
      return;
    }

    const timestamp = new Date().toISOString();
    const baseModel: HealthScoreModel = savedModel || createHealthModelFromScope(selectedScope);
    const nextModel: HealthScoreModel = {
      ...baseModel,
      document_title: documentTitle.trim() || getHealthModelDefaultTitle(selectedScope),
      document_content: content,
      model_key: modelKey ?? null,
      last_generated_at: lastGeneratedAt || baseModel.last_generated_at || null,
      updated_at: timestamp,
    };

    const nextModels = [...savedSettings.models];
    const existingIndex = nextModels.findIndex((model) => model.scope_key === selectedScope.scope_key);
    if (existingIndex >= 0) {
      nextModels[existingIndex] = nextModel;
    } else {
      nextModels.push(nextModel);
    }

    const nextSettings = normalizeHealthScoreSettings({
      ...savedSettings,
      updated_at: timestamp,
      models: nextModels,
    });

    try {
      const response = await saveMutation.mutateAsync(nextSettings);
      queryClient.setQueryData(["health-score-settings", workspaceId], response.data);
      queryClient.invalidateQueries({ queryKey: ["health-score-settings", workspaceId] });
      notify({
        type: "success",
        heading: "Draft saved",
        messageList: ["The health draft was saved. Sync it from the Health Scores page when you're ready."],
      });
    } catch (error: any) {
      const detail = typeof error?.response?.data?.detail === "string"
        ? error.response.data.detail
        : "I couldn't save the health draft right now.";

      notify({
        type: "failure",
        heading: "Save failed",
        messageList: [detail],
      });
    }
  }, [
    documentContent,
    documentTitle,
    modelKey,
    lastGeneratedAt,
    queryClient,
    saveMutation,
    savedModel,
    savedSettings,
    selectedScope,
    workspaceId,
  ]);

  const handleSubmit = useCallback(async (event?: FormEvent) => {
    event?.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isBusy) return;

    setInput("");
    void sendMessage(trimmed);
  }, [input, isBusy, sendMessage]);

  const isScopeLoading = settingsQuery.isLoading || businessContextQuery.isLoading || computedLabelsQuery.isLoading;
  const placeholder = isBusy
    ? "ORA is responding..."
    : "Ask ORA to help define this model, challenge the draft, or request examples...";

  if (settingsQuery.isError) {
    return (
      <Card className="mx-auto max-w-3xl">
        <CardHeader>
          <CardTitle>Health model builder</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">We couldn't load your health settings right now.</p>
        </CardContent>
      </Card>
    );
  }

  if (isScopeLoading && !selectedScope) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!selectedScope) {
    return (
      <Card className="mx-auto max-w-3xl">
        <CardHeader>
          <CardTitle>Health model not found</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This health model could not be found in your current settings.
          </p>
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Health Scores
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex h-full min-h-0 bg-background">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
        <div className="flex-shrink-0 border-b border-border bg-background px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleBack}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-3">
              <div className="rounded-lg border border-border bg-muted p-2">
                <Sparkles className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h1 className="text-[15px] font-semibold text-foreground">
                  Build {selectedScope.scope_label} Health Model
                </h1>
                <p className="text-[13px] text-muted-foreground">
                  Talk with ORA to build your health document. Save when ready.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto flex max-w-5xl flex-col gap-6">
            {streamedMessages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex gap-3",
                  message.role === "user" ? "justify-end" : "justify-start",
                )}
              >
                {message.role === "assistant" ? (
                  <ChatMessageAvatar role="assistant" className="mt-0.5" />
                ) : null}
                <div
                  className={cn(
                    "flex flex-col gap-1",
                    message.role === "user" ? "max-w-[28rem] items-end" : "max-w-[46rem] items-start",
                  )}
                >
                  <ChatSenderLabel role={message.role} />
                  <div
                    className={cn(
                      "w-full rounded-lg px-3.5 py-2.5",
                      message.role === "user"
                        ? "rounded-tr-sm bg-accent-tint text-foreground"
                        : "rounded-tl-sm border border-border bg-quaternary text-foreground",
                    )}
                  >
                    {message.isLoading ? (
                      <div className="flex gap-1 py-1">
                        <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: "0ms" }} />
                        <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: "150ms" }} />
                        <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: "300ms" }} />
                      </div>
                    ) : message.role === "user" ? (
                      <p className="whitespace-pre-wrap text-sm leading-[1.8] text-foreground">{message.content}</p>
                    ) : (
                      <MarkdownRenderer classname="text-sm [&_p]:text-sm [&_p]:leading-[1.8] [&_p]:text-foreground [&_li]:text-sm [&_li]:leading-[1.8] [&_li]:text-muted-foreground [&_ol]:text-sm [&_strong]:text-foreground">
                        {message.content}
                      </MarkdownRenderer>
                    )}
                  </div>
                </div>
                {message.role === "user" ? (
                  <ChatMessageAvatar role="user" userInitials={userInitials} className="mt-0.5" />
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-border bg-background p-4">
          <div className="mx-auto max-w-5xl">
            <form onSubmit={handleSubmit}>
              <div
                className={cn(
                  "relative rounded-lg border bg-background transition-all duration-200",
                  isFocused ? "border-foreground/30" : "border-border",
                )}
              >
                <input
                  type="text"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder={placeholder}
                  disabled={isBusy}
                  className="w-full bg-transparent px-5 py-4 pr-14 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isBusy}
                  className={cn(
                    "absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-[5px] transition-all",
                    input.trim() && !isBusy
                      ? "bg-foreground text-background hover:bg-foreground/90"
                      : "cursor-not-allowed bg-muted text-muted-foreground",
                  )}
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="mt-2.5 flex items-center gap-2 px-1 text-xs text-muted-foreground">
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    isBusy ? "animate-pulse bg-muted-foreground" : "bg-foreground",
                  )}
                />
                <span className={cn(isBusy ? "text-muted-foreground" : "text-foreground")}>
                  {latestDraft?.isStreaming
                    ? "Building draft..."
                    : saveMutation.isLoading
                      ? "Saving draft..."
                      : isStreaming || isAssistantTyping
                        ? "ORA is responding..."
                        : "ORA is ready"}
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>

      <aside className="flex w-96 shrink-0 flex-col border-l border-border bg-background">
        <div className="flex items-center gap-3 border-b border-border p-4">
          <div className="rounded-lg border border-border bg-muted p-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
          </div>
          <h2 className="text-sm font-semibold text-foreground">Health Document</h2>
          <Badge variant="outline" className="ml-auto rounded-full px-2 text-[11px]">
            {selectedScope.scope_type === "general"
              ? "General"
              : selectedScope.scope_type === "segment"
                ? "Segment"
                : "Lifecycle stage"}
          </Badge>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          <div className="space-y-2">
            <p className="text-[13px] font-medium text-foreground">Document title</p>
            <Input
              value={documentTitle}
              onChange={(event) => setDocumentTitle(event.target.value)}
              placeholder={getHealthModelDefaultTitle(selectedScope)}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <p className="text-[13px] font-medium text-foreground">AI Model</p>
            <ModelSelector
              surface="health"
              value={modelKey ?? undefined}
              onChange={(key) => setModelKey(key)}
              className="h-10 w-full justify-between rounded-md border border-border bg-background"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-[13px] font-medium text-foreground">Document content</p>
              {latestDraft?.isStreaming ? (
                <Badge variant="outline" className="animate-pulse rounded-full px-2 text-[11px]">
                  Building draft...
                </Badge>
              ) : null}
            </div>
            <Textarea
              value={documentContent}
              onChange={(event) => setDocumentContent(event.target.value)}
              placeholder={latestDraft?.isStreaming ? "ORA is preparing your draft..." : "Your draft will appear here."}
              className="min-h-[280px] font-mono text-[13px]"
            />
          </div>

          {lastGeneratedAt ? (
            <Badge variant="statusPill" status="success">
              Generated {formatHealthTimestamp(lastGeneratedAt)}
            </Badge>
          ) : null}
        </div>

        <div className="space-y-2 border-t border-border p-4">
          <Button className="w-full" onClick={() => void handleSaveDraft()} disabled={!documentContent.trim() || !hasDraftChanges || saveMutation.isLoading}>
            {saveMutation.isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save draft
              </>
            )}
          </Button>
        </div>
      </aside>

      <AlertDialog open={showUnsavedWarning} onOpenChange={setShowUnsavedWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to this health document. If you leave now, your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowUnsavedWarning(false); onBack?.(); }}>
              Leave without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HealthScoreChatBuilder;
